/**
 * apps/web entry point — the BOOT SEAM only.
 *
 * The composition root (`Composition`, `AppProviders`) lives in
 * `./Composition.tsx` (#236 follow-up, harness/composition convergence) so
 * the composition smoke family can mount the ACTUAL shipped wiring under
 * `StrictMode` in tests — this module stays import-side-effectful
 * (`createRoot` at module scope) and therefore untestable by design. See
 * `Composition.tsx` for the layering diagram, the composition decisions,
 * and the ports composition documentation.
 *
 * Two boot paths:
 *   1. The CMS iframe preview document (`PREVIEW_FRAME_PATH`, D-121): a
 *      code-split sibling composition root, dynamically imported.
 *   2. The member/admin app: StrictMode + AppErrorBoundary + AppProviders +
 *      Composition.
 */

// Design-token substrate. `tokens.css` is the single CSS entry Tailwind
// v4's Vite plugin processes. It @imports `shell/shell.css` at the top
// for the base reset + the `.shell` class; then `@import "tailwindcss";`
// enables utility generation, and the `@theme` block publishes the
// design tokens.
import './tokens/tokens.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AppProviders, Composition } from './Composition.js';
// The preview-frame mount path. This is a side-effect-free CONSTANTS module
// (only a string literal, no imports) — importing it here does NOT reach the
// preview React tree, so it cannot fold the code-split preview chunk into the
// member entry graph. The actual preview entry is reached ONLY via the dynamic
// `import()` below (FR-018); `previewFramePath.ts` keeps the path a single
// source of truth shared with `PreviewFrameApp.tsx`.
import { PREVIEW_FRAME_PATH } from './components/cms/preview-frame/previewFramePath.js';
import { AppErrorBoundary } from './shell/AppErrorBoundary.js';

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in index.html');
}

/**
 * Boot seam for the CMS iframe preview document (D-121 phase 1). When the
 * document is loaded at the exact `/admin/cms/preview-frame` path (the editor
 * embeds it in an `<iframe>`, Phase 2), mount the PREVIEW entry — its own
 * composition root (StrictMode + AppErrorBoundary + QueryClient + admin gate +
 * preview ports + MemoryRouter + the real member RouteRegistry) — INSTEAD of
 * the normal member `Composition`.
 *
 * The dynamic `import()` is the code-split boundary: Vite + Rollup emit the
 * preview entry and everything it statically reaches as a chunk that is NOT in
 * the member entry graph, so a member session downloads zero bytes of preview
 * code (FR-018, mirroring `shell/lazyAdminRoutes.ts`). This entry carries only
 * the path check + the import callsite. This is NOT a `ROUTES` descriptor — the
 * preview document is a sibling composition root, not a route in the admin app.
 *
 * `main.tsx` is not `async`, so the import is launched fire-and-forget with an
 * explicit `void` + a `catch`. `PREVIEW_FRAME_PATH` is imported from the
 * side-effect-free `previewFramePath.ts` constants module (a single source of
 * truth shared with `PreviewFrameApp.tsx`) — that import does NOT reach the
 * preview tree, so the code split is preserved.
 */
if (window.location.pathname === PREVIEW_FRAME_PATH) {
  void import('./components/cms/preview-frame/mountPreviewFrame.js')
    .then(({ mountPreviewFrame }) => {
      mountPreviewFrame(rootElement);
    })
    .catch((error: unknown) => {
      // The preview entry's dynamic import failed (chunk fetch / parse error).
      // Throwing here does NOT reach `AppErrorBoundary` (that boundary only
      // catches throws INSIDE the React tree, and this callback runs outside
      // it); the rejection surfaces as a browser `unhandledrejection` — a blank
      // preview document plus a devtools-console error, with no in-app fallback
      // UI. That is acceptable for an internal admin-only tool (~5 users,
      // D-121): the editor sees an empty iframe and reloads. The throw still
      // preserves the failure for the console rather than swallowing it (there
      // is no client logger in `apps/web`).
      throw new Error('Failed to load the CMS preview frame entry', { cause: error });
    });
} else {
  createRoot(rootElement).render(
    <StrictMode>
      <AppErrorBoundary>
        <AppProviders>
          <Composition />
        </AppProviders>
      </AppErrorBoundary>
    </StrictMode>,
  );
}
